/**
 * The vocabularies an assistant's configuration is written in.
 *
 * Primary's models re-export these rather than declaring their own, so the value a row holds and
 * the value this contract publishes cannot drift.
 */
/** Who speaks first when a call connects, and whether that greeting is scripted. */
export var AssistantFirstMessageMode;
(function (AssistantFirstMessageMode) {
    AssistantFirstMessageMode["AssistantSpeaksFirst"] = "AssistantSpeaksFirst";
    AssistantFirstMessageMode["AssistantSpeaksFirstWithModelGeneratedMessage"] = "AssistantSpeaksFirstWithModelGeneratedMessage";
    AssistantFirstMessageMode["AssistantWaitsForUser"] = "AssistantWaitsForUser";
})(AssistantFirstMessageMode || (AssistantFirstMessageMode = {}));
/** A switch on one optional call behavior. */
export var AssistantOptionMode;
(function (AssistantOptionMode) {
    AssistantOptionMode["Enabled"] = "Enabled";
    AssistantOptionMode["Disabled"] = "Disabled";
})(AssistantOptionMode || (AssistantOptionMode = {}));
/** Ambience mixed under the assistant's voice. */
export var VoiceCallBackgroundSound;
(function (VoiceCallBackgroundSound) {
    VoiceCallBackgroundSound["Off"] = "Off";
    VoiceCallBackgroundSound["Office"] = "Office";
})(VoiceCallBackgroundSound || (VoiceCallBackgroundSound = {}));
/** The fixed prompts the assistant may speak when the caller has gone quiet. */
export var AssistantIdleMessage;
(function (AssistantIdleMessage) {
    AssistantIdleMessage["AreYouStillThere"] = "AreYouStillThere";
    AssistantIdleMessage["IsThereAnythingElseYouNeedHelpWith"] = "IsThereAnythingElseYouNeedHelpWith";
    AssistantIdleMessage["FeelFreeToAskMeAnyQuestions"] = "FeelFreeToAskMeAnyQuestions";
    AssistantIdleMessage["HowCanIAssistYouFurther"] = "HowCanIAssistYouFurther";
    AssistantIdleMessage["LetMeKnowIfThereIsAnythingYouNeed"] = "LetMeKnowIfThereIsAnythingYouNeed";
    AssistantIdleMessage["ImStillHereIfYouNeedAssistance"] = "ImStillHereIfYouNeedAssistance";
    AssistantIdleMessage["ImReadyToHelpWheneverYouAre"] = "ImReadyToHelpWheneverYouAre";
    AssistantIdleMessage["IsThereSomethingSpecificYouAreLookingFor"] = "IsThereSomethingSpecificYouAreLookingFor";
    AssistantIdleMessage["ImHereToHelpWithAnyQuestionsYouHave"] = "ImHereToHelpWithAnyQuestionsYouHave";
})(AssistantIdleMessage || (AssistantIdleMessage = {}));
/** The language the assistant speaks and listens in, as an ISO 639-1 code. */
export var AssistantLanguage;
(function (AssistantLanguage) {
    AssistantLanguage["Bulgarian"] = "bg";
    AssistantLanguage["Chinese"] = "zh";
    AssistantLanguage["Czech"] = "cs";
    AssistantLanguage["Danish"] = "da";
    AssistantLanguage["Dutch"] = "nl";
    AssistantLanguage["English"] = "en";
    AssistantLanguage["Finnish"] = "fi";
    AssistantLanguage["French"] = "fr";
    AssistantLanguage["German"] = "de";
    AssistantLanguage["Hindi"] = "hi";
    AssistantLanguage["Hungarian"] = "hu";
    AssistantLanguage["Indonesian"] = "id";
    AssistantLanguage["Italian"] = "it";
    AssistantLanguage["Japanese"] = "ja";
    AssistantLanguage["Korean"] = "ko";
    AssistantLanguage["Malay"] = "ms";
    AssistantLanguage["Multilingual"] = "multi";
    AssistantLanguage["Polish"] = "pl";
    AssistantLanguage["Portuguese"] = "pt";
    AssistantLanguage["Romanian"] = "ro";
    AssistantLanguage["Russian"] = "ru";
    AssistantLanguage["Slovak"] = "sk";
    AssistantLanguage["Spanish"] = "es";
    AssistantLanguage["Swedish"] = "sv";
    AssistantLanguage["Tamil"] = "ta";
    AssistantLanguage["Turkish"] = "tr";
    AssistantLanguage["Ukrainian"] = "uk";
    AssistantLanguage["Vietnamese"] = "vi";
})(AssistantLanguage || (AssistantLanguage = {}));
/** Who serves the language model behind the assistant. */
export var LlmModelProvider;
(function (LlmModelProvider) {
    LlmModelProvider["OpenAi"] = "OpenAi";
    LlmModelProvider["Anthropic"] = "Anthropic";
    LlmModelProvider["CustomLlm"] = "CustomLlm";
})(LlmModelProvider || (LlmModelProvider = {}));
/** Whether the model reasons over transcribed text or over the audio itself. */
export var LlmModelType;
(function (LlmModelType) {
    LlmModelType["TextBased"] = "TextBased";
    LlmModelType["RealtimeVoice"] = "RealtimeVoice";
})(LlmModelType || (LlmModelType = {}));
/** Who synthesizes the assistant's voice. */
export var VoicePersonaProvider;
(function (VoicePersonaProvider) {
    VoicePersonaProvider["ElevenLabs"] = "ElevenLabs";
    VoicePersonaProvider["OpenAi"] = "OpenAi";
    VoicePersonaProvider["Cartesia"] = "Cartesia";
})(VoicePersonaProvider || (VoicePersonaProvider = {}));
/** The model types a voice can be paired with. */
export var LlmModelCompatibility;
(function (LlmModelCompatibility) {
    LlmModelCompatibility["RealtimeVoiceOnly"] = "RealtimeVoiceOnly";
    LlmModelCompatibility["All"] = "All";
    LlmModelCompatibility["TextBasedOnly"] = "TextBasedOnly";
})(LlmModelCompatibility || (LlmModelCompatibility = {}));
/** What a tool linked to an assistant does when the assistant calls it. */
export var AssistantToolType;
(function (AssistantToolType) {
    AssistantToolType["authenticateInboundPatient"] = "authenticateInboundPatient";
    AssistantToolType["authenticateKnownPatient"] = "authenticateKnownPatient";
    AssistantToolType["bookSelectedSlot"] = "bookSelectedSlot";
    AssistantToolType["createPatient"] = "createPatient";
    AssistantToolType["getAppointments"] = "getAppointments";
    AssistantToolType["getEncounterDetails"] = "getEncounterDetails";
    AssistantToolType["getEncounters"] = "getEncounters";
    AssistantToolType["getInsurance"] = "getInsurance";
    AssistantToolType["getOrders"] = "getOrders";
    AssistantToolType["getPatient"] = "getPatient";
    AssistantToolType["getPracticeDetails"] = "getPracticeDetails";
    AssistantToolType["getTimeDifference"] = "getTimeDifference";
    AssistantToolType["lookupPatient"] = "lookupPatient";
    AssistantToolType["searchForOpenSlots"] = "searchForOpenSlots";
    AssistantToolType["sendInboundAuthCode"] = "sendInboundAuthCode";
    AssistantToolType["sendSms"] = "sendSms";
    AssistantToolType["traverseDecisionTree"] = "traverseDecisionTree";
    AssistantToolType["EndCall"] = "EndCall";
    AssistantToolType["TransferCall"] = "TransferCall";
    AssistantToolType["NotableTransferCall"] = "NotableTransferCall";
    AssistantToolType["AssistantHandoff"] = "AssistantHandoff";
})(AssistantToolType || (AssistantToolType = {}));
/** How a transfer tool hands the caller over. */
export var VoiceCallTransferCallMode;
(function (VoiceCallTransferCallMode) {
    VoiceCallTransferCallMode["BlindTransfer"] = "BlindTransfer";
    VoiceCallTransferCallMode["BlindTransferAddSummaryToSipHeader"] = "BlindTransferAddSummaryToSipHeader";
    VoiceCallTransferCallMode["WarmTransferSayMessage"] = "WarmTransferSayMessage";
    VoiceCallTransferCallMode["WarmTransferSaySummary"] = "WarmTransferSaySummary";
    VoiceCallTransferCallMode["WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage"] = "WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage";
    VoiceCallTransferCallMode["WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary"] = "WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary";
    VoiceCallTransferCallMode["WarmTransferTwiml"] = "WarmTransferTwiml";
    VoiceCallTransferCallMode["WarmTransferExperimental"] = "WarmTransferExperimental";
    VoiceCallTransferCallMode["TransferWithDtmfCallToken"] = "TransferWithDtmfCallToken";
    VoiceCallTransferCallMode["BlindTransferWithTokenInSipHeader"] = "BlindTransferWithTokenInSipHeader";
    VoiceCallTransferCallMode["BlindTransferWithVoiceCallIdInSipHeader"] = "BlindTransferWithVoiceCallIdInSipHeader";
})(VoiceCallTransferCallMode || (VoiceCallTransferCallMode = {}));
/** Whether a transfer destination is dialed as a phone number or as a SIP address. */
export var VoiceCallTransferCallToolDestinationType;
(function (VoiceCallTransferCallToolDestinationType) {
    VoiceCallTransferCallToolDestinationType["Number"] = "Number";
    VoiceCallTransferCallToolDestinationType["Sip"] = "Sip";
})(VoiceCallTransferCallToolDestinationType || (VoiceCallTransferCallToolDestinationType = {}));
/** How much of the conversation a handoff carries to the assistant receiving it. */
export var AssistantHandoffContextEngineeringPlanType;
(function (AssistantHandoffContextEngineeringPlanType) {
    AssistantHandoffContextEngineeringPlanType["All"] = "all";
    AssistantHandoffContextEngineeringPlanType["UserAndAssistantMessages"] = "userAndAssistantMessages";
    AssistantHandoffContextEngineeringPlanType["None"] = "none";
    AssistantHandoffContextEngineeringPlanType["LastNMessages"] = "lastNMessages";
})(AssistantHandoffContextEngineeringPlanType || (AssistantHandoffContextEngineeringPlanType = {}));
/** The type of one value a structured output extracts from a conversation. */
export var StructuredOutputSchemaType;
(function (StructuredOutputSchemaType) {
    StructuredOutputSchemaType["String"] = "string";
    StructuredOutputSchemaType["Boolean"] = "boolean";
})(StructuredOutputSchemaType || (StructuredOutputSchemaType = {}));
//# sourceMappingURL=constants.js.map